// supervisor-forensics-archival.ts
//
// Wires attempt-forensics.ts (P6 Agent B, previously never called from
// production — code-reviewer 260909-p6-m2 H2) into the control loop: on a
// `contract_error` or `recovering` verdict, archive the worktree diff +
// binding evidence BEFORE the mission worktree can be touched again.
//
// Ordering is the whole point: `archiveAttemptForensics` (this module) MUST
// run in the SAME tick as the verdict, before `runOneSupervisorTick`'s NEXT
// invocation calls `recoverOwnedCheckout` at its top (supervisor-loop.ts) —
// that reset/clean would erase the exact tracked+untracked diff forensics
// needs to capture. Calling this at the end of THIS tick (not the start of
// the next) guarantees that ordering without adding any new event-journal
// bookkeeping.

import { archiveAttemptForensics } from "./attempt-forensics.js";
import { SecretRedactor } from "./secret-redactor.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";

export interface ArchiveForensicsForVerdictInput {
  controlRoot: string;
  repoDir: string;
  ticketId: string;
  attemptNo: number;
  cycle: AttemptCycleResult;
}

function forensicsReasonFor(cycle: AttemptCycleResult): string | undefined {
  if (cycle.kind === "contract_error") return `contract_error: ${cycle.reason}`;
  if (cycle.verdict.kind === "contract_error" || cycle.verdict.kind === "recovering") {
    return `${cycle.verdict.kind}: ${cycle.verdict.reasons.join("; ")}`;
  }
  // P13 Agent A (found dogfooding a REAL mission, not fixed at design time):
  // supervisor-attempt-cycle.ts's "no result to verify" branch (session-
  // runner.ts's outcome !== "ok", e.g. no_result_json_found/
  // invalid_structured_output_schema) ALWAYS wraps the verdict as
  // `{kind:"rejected", reasons:[...]}` — never "contract_error" — because
  // the verifier is never even called (nothing was produced to diff). That
  // made the two checks above never fire for exactly the outcome class this
  // phase's forensics/prompt work is about: a real mission produced 0
  // result-schema-error.json files despite 3 attempts hitting
  // invalid_structured_output_schema/no_result_json_found (verified via a
  // real dogfood run — see reports/phase-13-agent-a-evidence.md). Checking
  // the underlying sessionResult.outcome directly (independent of how the
  // verifier chose to wrap it) is what actually gets forensics + the
  // rawResultTail/schemaErrorPaths evidence onto disk for this case.
  if (cycle.kind === "verified" && cycle.sessionResult.outcome === "contract_error") {
    return `contract_error: ${cycle.sessionResult.reason ?? "unknown"}`;
  }
  return undefined;
}

// P13 Agent A: short "missing: a; invalid: b" summary from a validated
// result's zod issue paths, for `mission-state-reducer.ts`'s
// `ticket_last_verdict.schema_error_detail` (status/dashboard surfacing —
// brief example: "missing: evidence[0].sha256; invalid: changed_files[2]").
// zod's own issue.code doesn't cleanly separate "missing" vs "invalid" for
// every case, so this only distinguishes the one case that matters for the
// brief's example and is unambiguous from the code alone: `invalid_type`
// with `received: "undefined"` reads as "missing"; everything else as
// "invalid" — good enough for an operator-facing hint, not a parser.
export function formatSchemaErrorDetail(paths: string[] | undefined): string | undefined {
  if (paths === undefined || paths.length === 0) return undefined;
  return `invalid: ${paths.join(", ")}`;
}

// Instrumentation 2026-09-15 (dogfood phần 3, câu hỏi treo 1): the journal
// copy of the worker's raw `result` tail is capped tighter than the 4000-char
// forensics copy — journal.jsonl is append-only forever and replayed on every
// `status`, so it gets the last 2 KiB only. Sized to hold the trailing
// "here is my answer …" paragraph a `no_result_json_found` session ends with
// (real T-0103 sample was < 1 KB), not the whole transcript.
export const RAW_RESULT_TAIL_JOURNAL_MAX_CHARS = 2048;

/**
 * Prepares a session's `rawResultTail` for `attempt_finished.raw_result_tail`.
 * Redacts FIRST (pattern-based SecretRedactor: the stream consumer already
 * removed the mission's known secret values before the text was ever parsed)
 * and caps SECOND — capping first could cut a token in half and leave a
 * suffix the pattern no longer recognises. `undefined` in → `undefined` out,
 * so a missing_result_event/two_result_events verdict never gains an empty
 * string. Reducer redacts once more on the way into the projection
 * (mission-state-reducer.ts) — defense in depth, same as schema_error_detail.
 */
export function formatRawResultTailForJournal(tail: string | undefined): string | undefined {
  if (tail === undefined) return undefined;
  const redactor = new SecretRedactor({ secretValues: [] });
  const redacted = redactor.feed(tail) + redactor.flush();
  return redacted.length > RAW_RESULT_TAIL_JOURNAL_MAX_CHARS
    ? redacted.slice(-RAW_RESULT_TAIL_JOURNAL_MAX_CHARS)
    : redacted;
}

// P13 Agent A: session-runner.ts only ever populates rawResultTail/
// schemaErrorPaths for a session-level contract_error (see
// session-runner-stream-consumer.ts's finalizeResult) — a dispatch-time
// contract_error (`cycle.kind === "contract_error"`, e.g. missing adapter)
// never reaches the session runner at all, so there is nothing to surface
// here for that branch.
function schemaErrorEvidenceFor(cycle: AttemptCycleResult): {
  rawResultTail?: string;
  schemaErrorPaths?: string[];
} {
  if (cycle.kind !== "verified") return {};
  return {
    rawResultTail: cycle.sessionResult.rawResultTail,
    schemaErrorPaths: cycle.sessionResult.schemaErrorPaths,
  };
}

/**
 * Archives forensics evidence for a `contract_error`/`recovering` verdict.
 * No-op (returns `undefined`) for every other verdict kind — forensics only
 * exists to preserve evidence of a REJECTED/tampered attempt, not every run.
 */
export async function archiveForensicsForVerdict(
  input: ArchiveForensicsForVerdictInput,
): Promise<void> {
  const reason = forensicsReasonFor(input.cycle);
  if (reason === undefined) return;

  await archiveAttemptForensics({
    controlRoot: input.controlRoot,
    repoDir: input.repoDir,
    attemptId: `${input.ticketId}-${input.attemptNo}`,
    reason,
    ...schemaErrorEvidenceFor(input.cycle),
  });
}
